import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { COLLECTION_SORT_BY } from '../../../shared/common/SortByDropDown';
import ContentBaseLayout from '../../libx/shared/ContentBaseLayout';
import FilterBar from '../../../shared/common/FilterBar';
import ProductList from '../../../shared/common/ProductList';
import Pagination from '../../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import CollectionListItem from '../../../shared/common/ListItems/CollectionListItem';
import {
    invalidateCompleteCollectionProducts,
    useCompleteCollectionProducts
} from './api/useCompleteCollectionProducts';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { useParams } from 'react-router';
import { getLoanFormat } from '../../../shared/utils/getLoanFormat';
import { AgeCategory } from '../../../../models/AgeCategory';
import { CompleteCollectionHeader } from './CompleteCollectionHeader';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchCompleteCollectionAggregationsQuickSearch } from '../../../api/collection/complete-collection/fetchCompleteCollectionAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { AuthenticationState } from '../../../store/authentication/state';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchCompleteCollectionAggregations } from '../../../api/collection/complete-collection/fetchCompleteCollectionAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useAddProductsToGroupFromCollection } from '../../../api/library-managed/useAddProductsToGroupFromCollection';
import { CollectionProductTile } from '../CollectionProductTile';
import classNames from 'classnames';
import { useAddBulkFromCollectionToCart } from '../../../api/shopping-cart/useAddFromCollectionToCart';
import { SOURCE_COLLECTION } from '../../../store/constants';
import { useAddBulkFromCollectionToList } from '../../../api/selection-list/useAddFromCollectionToList';
import { SelectionList } from '../../../../models/SelectionList';
import { Article, SubscriptionPeriodType } from '../../../../models/Article';
import { CopyDetailsPopup } from '../copyDetailsPopup/CopyDetailsPopup';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { PriceTier } from '../../../../models/PriceTier';
import { useProductSelection } from '../../../shared/product-selection/useProductSelection';
import { hasProductSelection, nothingSelected } from '../../../shared/product-selection/ProductSelectionUtils';
import { UnavailableProductsModal } from '../../../shared/common/UnavailableProductsModal';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../shared/loanFormat/constants';
import { useAuthentication } from '../../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../shared/filter/useProductFilters';

const FilterBarWithSpace = styled(FilterBar)`
    padding: 0 0 25px 0;
`;

const DEFAULT_PAGE_SIZE = 54;

export const CompleteCollectionPage = () => {
    const [product, setProduct] = useState<(ProductMetaData & Article) | undefined>(undefined);

    const authentication = useAuthentication();
    const { toggleSelectionMode, cancelSelection, selectAll, productSelection, onSelectButtonClick } =
        useProductSelection();
    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD,
        defaultPageSize: DEFAULT_PAGE_SIZE
    });

    const { productFilter } = useProductFilters();
    const { display } = useDisplayTypeSearchParam();

    const { loanFormat: loanFormatPathParam } = useParams<'loanFormat'>();
    const loanFormat = getLoanFormat(loanFormatPathParam);
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { products, isLoading: isProductsLoading } = useCompleteCollectionProducts({
        pagination: { page, pageSize, sortBy },
        filterMap: productFilter,
        loanFormat: loanFormat.requestKey,
        ageCategory
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => completeCollectionPageFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'completeCollectionPage',
            loanFormat.requestKey!,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups
        ],
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchCompleteCollectionAggregations({ ...options, ageCategory, loanFormat: loanFormat.requestKey! }),
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchCompleteCollectionAggregationsQuickSearch({
                ...options,
                ageCategory,
                loanFormat: loanFormat.requestKey!
            }),
        [ageCategory, loanFormat.requestKey]
    );
    //endregion

    const { addToGroupBulk } = useAddProductsToGroupFromCollection({
        source: SOURCE_COLLECTION,
        ageCategory,
        productSelection,
        loanFormat,
        onSuccess: () => {
            cancelSelection();
        },
        productFilter
    });

    const { isLoading: isLoadingAddProductsToCart, addToCartBulk } = useAddBulkFromCollectionToCart({
        loanFormat,
        ageCategory,
        source: SOURCE_COLLECTION,
        productFilter,
        onSuccess: () => {
            invalidateCompleteCollectionProducts().then();
            cancelSelection();
        },
        productSelection
    });

    const { isLoading: isLoadingAddProductsToList, addToListBulk } = useAddBulkFromCollectionToList({
        loanFormat,
        ageCategory,
        source: SOURCE_COLLECTION,
        productSelection,
        onSuccess: () => {
            cancelSelection();
        },
        productFilter
    });

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateCompleteCollectionProducts().then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const onBulkAddToList = async (
        selectionList: SelectionList,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => {
        return addToListBulk(selectionList.id, performChecks, priceTier, subscriptionPeriodType);
    };

    const loading = isProductsLoading || isLoadingAddProductsToCart || isLoadingAddProductsToList;

    return (
        <>
            <ContentBaseLayout
                documentTitle={'Complete Collection | BorrowBox Connect'}
                loading={loading}
                topComponent={
                    <>
                        <CompleteCollectionHeader
                            isInSelectionMode={hasProductSelection(productSelection)}
                            onBulkAddToCart={addToCartBulk}
                            onBulkAddToList={onBulkAddToList}
                            cancelSelectionMode={cancelSelection}
                            productSelection={productSelection}
                            onBulkAddToGroup={addToGroupBulk}
                            containsEpressProducts={loanFormat.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey}
                            isNothingSelected={nothingSelected(productSelection)}
                        />
                        <FilterBarWithSpace
                            toggleSelectionMode={toggleSelectionMode}
                            selectAll={() => selectAll(products.count)}
                            sorting={COLLECTION_SORT_BY}
                            sortBy={sortBy}
                            hideGroupBy={true}
                            displayType={display}
                            totalProducts={products.count}
                            pageSize={pageSize}
                        />
                    </>
                }
                sidebarComponent={
                    displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null
                }
                contentComponent={
                    isProductsLoading || products.list.length > 0 ? (
                        <div className="ca-main">
                            <div className={classNames({ 'min-h-screen': isProductsLoading }, 'ca-main-inner')}>
                                <ProductList
                                    displayType={display}
                                    products={products.list}
                                    onAddPrice={changePrice}
                                    onSelectProduct={onSelectButtonClick}
                                    productSelection={productSelection}
                                    isProductSelectable={true}
                                >
                                    {({ key, isListView, ...value }) =>
                                        isListView ? (
                                            <CollectionListItem
                                                {...value}
                                                key={key}
                                                showManagePopup={() => setProduct(value.product)}
                                            />
                                        ) : (
                                            <CollectionProductTile
                                                {...value}
                                                key={key}
                                                showManagePopup={() => setProduct(value.product)}
                                                isProductGroupTile={false}
                                            />
                                        )
                                    }
                                </ProductList>
                                <Pagination totalItems={products.count} pageSize={pageSize} />
                            </div>
                        </div>
                    ) : (
                        <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                    )
                }
                modalComponent={
                    product ? (
                        <CopyDetailsPopup
                            onClosePopup={() => setProduct(undefined)}
                            onAddPrice={changePrice}
                            product={product}
                            shopLanguage={undefined}
                        />
                    ) : null
                }
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};

const completeCollectionPageFilterGroups = (authentication: AuthenticationState, ageCategory?: AgeCategory) => [
    ...defaultVisibleFilterGroups(authentication, ageCategory),
    'stockUsageCondition'
];
